import { CssBaseline, GlobalStyles, ThemeProvider } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../store/hooks";
import { createAppTheme } from "./createAppTheme";
import {
  createCssVariableMap,
  createThemeTokens,
  getSystemThemeMode,
  resolveThemeMode,
} from "./themeTokens";

const ThemeRoot = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAppSelector((state) => state.auth);
  const { themeMode, fontScale, bookingColors } = useAppSelector(
    (state) => state.settings,
  );

  const [systemThemeMode, setSystemThemeMode] = useState<"light" | "dark">(
    getSystemThemeMode,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)");

    if (!mediaQuery) {
      return;
    }

    const listener = () => {
      setSystemThemeMode(getSystemThemeMode());
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", listener);

      return () => {
        mediaQuery.removeEventListener("change", listener);
      };
    }

    mediaQuery.addListener(listener);

    return () => {
      mediaQuery.removeListener(listener);
    };
  }, []);

  const effectiveThemeMode = resolveThemeMode(themeMode, systemThemeMode);

  const tokens = useMemo(() => {
    return createThemeTokens({
      mode: effectiveThemeMode,
      role: user?.role,
      bookingColors,
    });
  }, [bookingColors, effectiveThemeMode, user?.role]);

  const theme = useMemo(() => {
    return createAppTheme({
      tokens,
      fontScale,
      mode: effectiveThemeMode,
    });
  }, [effectiveThemeMode, fontScale, tokens]);

  const cssVariables = useMemo(() => {
    return createCssVariableMap({
      tokens,
      fontScale,
      mode: effectiveThemeMode,
    });
  }, [effectiveThemeMode, fontScale, tokens]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          ":root": cssVariables,
          html: {
            minHeight: "100%",
            backgroundColor: "var(--rp-background)",
            color: "var(--rp-text-primary)",
            transition:
              "background-color 180ms ease, color 180ms ease, font-size 180ms ease",
          },
          body: {
            minHeight: "100%",
            backgroundColor: "var(--rp-background)",
            color: "var(--rp-text-primary)",
            transition: "background-color 180ms ease, color 180ms ease",
          },
          "#root": {
            minHeight: "100%",
            backgroundColor: "var(--rp-background)",
          },
          "::selection": {
            backgroundColor: "var(--rp-primary-soft)",
            color: "var(--rp-text-primary)",
          },
        }}
      />
      {children}
    </ThemeProvider>
  );
};

export default ThemeRoot;
